import React, { useEffect, useRef, useMemo } from 'react'
import katex from 'katex'
import 'katex/dist/katex.min.css'

interface KatexRendererProps {
  text: string
  className?: string
}

// Check if text contains Korean characters
const containsKorean = (text: string): boolean => {
  // Korean Unicode ranges: Hangul Syllables (AC00-D7AF), Hangul Jamo (1100-11FF), 
  // Hangul Compatibility Jamo (3130-318F), Hangul Jamo Extended (A960-A97F, D7B0-D7FF)
  return /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F\uA960-\uA97F\uD7B0-\uD7FF]/.test(text)
}

/**
 * Convert LaTeX commands back to Unicode for use inside \text{}
 * Because \text{} renders content as plain text, LaTeX commands like \times show literally
 */
const latexToUnicodeForText = (content: string): string => {
  return content
    // Math operators
    .replace(/\\times\s*/g, '×')
    .replace(/\\div\s*/g, '÷')
    .replace(/\\pm\s*/g, '±')
    .replace(/\\mp\s*/g, '∓')
    .replace(/\\cdot\s*/g, '·')
    .replace(/\\ast\s*/g, '*')
    // Spacing commands -> regular spaces
    .replace(/\\;/g, ' ')
    .replace(/\\:/g, ' ')
    .replace(/\\,/g, ' ')
    .replace(/\\!/g, '')
    .replace(/\\quad\s*/g, '  ')
    .replace(/\\qquad\s*/g, '    ')
    .replace(/\\hspace\{[^}]*\}/g, ' ')
    // Comparison operators
    .replace(/\\le\s*/g, '≤')
    .replace(/\\leq\s*/g, '≤')
    .replace(/\\ge\s*/g, '≥')
    .replace(/\\geq\s*/g, '≥')
    .replace(/\\ne\s*/g, '≠')
    .replace(/\\neq\s*/g, '≠')
    // Greek letters commonly used
    .replace(/\\alpha\s*/g, 'α')
    .replace(/\\beta\s*/g, 'β')
    .replace(/\\gamma\s*/g, 'γ')
    .replace(/\\pi\s*/g, 'π')
    // Clean up any remaining backslash escapes for quotes
    .replace(/\\'/g, "'")
}

/**
 * Normalize KaTeX math expressions to fix common issues:
 * 1. \hspace without braces: \hspace0.2ex -> \hspace{0.2ex}
 * 2. Korean text in \underline{}: \underline{작은} -> \underline{\text{작은}}
 * 3. \underline\textbf{...} -> \underline{\textbf{...}}
 */
export const normalizeKatexMath = (math: string): string => {
  let normalized = math

  // Fix \hspace without braces (handles both integer and decimal)
  // \hspace0.2ex -> \hspace{0.2ex}
  normalized = normalized.replace(/\\hspace(\d+\.?\d*)([a-z]+)/g, '\\hspace{$1$2}')

  // Fix \underline\textbf{...} -> \underline{\textbf{...}}
  // This handles cases like \underline\textbf{않은}
  normalized = normalized.replace(
    /\\underline\\textbf\{([^}]*)\}/g,
    (_, content) => {
      if (containsKorean(content)) {
        // Convert LaTeX commands to Unicode for text mode
        const textContent = latexToUnicodeForText(content)
        return `\\underline{\\textbf{\\text{${textContent}}}}`
      }
      return `\\underline{\\textbf{${content}}}`
    }
  )

  // Fix Korean text in \underline{}: \underline{작은} -> \underline{\text{작은}}
  // Match \underline{...} where content contains Korean
  normalized = normalized.replace(
    /\\underline\{([^}]*)\}/g,
    (match, content) => {
      // Skip if already wrapped with \text or \textbf
      if (content.startsWith('\\text') || content.startsWith('\\textbf')) {
        return match
      }
      if (containsKorean(content)) {
        // Convert LaTeX commands to Unicode for text mode
        const textContent = latexToUnicodeForText(content)
        return `\\underline{\\text{${textContent}}}`
      }
      return match
    }
  )

  return normalized
}

// Render plain text with line breaks
const renderPlainText = (text: string, keyPrefix: string): React.ReactNode => {
  if (!text) return null

  const lines = text.split('\n')
  return lines.map((line, idx) => (
    <React.Fragment key={`${keyPrefix}_line_${idx}`}>
      {line}
      {idx < lines.length - 1 && <br />}
    </React.Fragment>
  ))
}

// Parse text to extract text, math, and fraction parts
interface TextPart {
  type: 'text' | 'math' | 'frac'
  content: string
  key: string
  // For frac type
  numerator?: string
  denominator?: string
}

// Decode encoded blanks from textFormatter
const decodeBlankMarkers = (text: string): string => {
  return text
    .replace(/__LBLANK__/g, '{{')
    .replace(/__RBLANK__/g, '}}')
}

// CSS Fraction component (for fractions with blanks)
// Uses same DOM structure as legacy quiz-parser.tsx (className-only, no inline styles)
const CssFraction: React.FC<{ numerator: string; denominator: string }> = ({
  numerator,
  denominator,
}) => {
  // Decode blank markers that were encoded in textFormatter to prevent regex splitting
  const decodedNum = decodeBlankMarkers(numerator)
  const decodedDen = decodeBlankMarkers(denominator)
  
  return (
    <div className="problem-frac-wrap">
      <div className="problem-frac-numerator">
        <KatexRenderer text={decodedNum} />
      </div>
      <div className="problem-frac-denominator">
        <KatexRenderer text={decodedDen} />
      </div>
    </div>
  )
}

const parseTextWithMath = (text: string): TextPart[] => {
  // Combined regex for $$...$$ and [FRAC:...:FRAC]
  const combinedReg = /(\$\$[^$]+\$\$|\[FRAC:[^:]*:\/:.*?:FRAC\])/gim
  const parts: TextPart[] = []
  
  let lastIndex = 0
  let match: RegExpExecArray | null
  let idx = 0

  while ((match = combinedReg.exec(text)) !== null) {
    // Add text before this expression
    if (match.index > lastIndex) {
      const textContent = text.slice(lastIndex, match.index)
      parts.push({
        type: 'text',
        content: textContent,
        key: `text-${idx++}`,
      })
    }

    const matched = match[0]

    // Check if it's a FRAC marker
    if (matched.startsWith('[FRAC:')) {
      // Parse [FRAC:numerator:/:denominator:FRAC] - separator is :/:
      const fracMatch = matched.match(/\[FRAC:(.*?):\/:(.*?):FRAC\]/)
      if (fracMatch) {
        parts.push({
          type: 'frac',
          content: matched,
          key: `frac-${idx++}`,
          numerator: fracMatch[1],
          denominator: fracMatch[2],
        })
      }
    } else if (matched.startsWith('$$')) {
      // It's a math expression
      const mathContent = matched.slice(2, -2) // Remove $$ from both ends
      parts.push({
        type: 'math',
        content: mathContent,
        key: `math-${idx++}`,
      })
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text after last expression
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex),
      key: `text-${idx++}`,
    })
  }

  return parts
}

// KaTeX math component with error handling
interface KaTeXMathProps {
  math: string
  className?: string
}

const KaTeXMath: React.FC<KaTeXMathProps> = ({ math, className }) => {
  const containerRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!containerRef.current || !math) return

    const normalizedMath = normalizeKatexMath(math)

    try {
      // Try rendering with normalized math first
      katex.render(normalizedMath, containerRef.current, {
        throwOnError: false,
        displayMode: false,
      })
    } catch {
      // If normalization didn't help, show original text as fallback
      if (containerRef.current) {
        containerRef.current.textContent = `$$${math}$$`
      }
    }
  }, [math])

  return <span ref={containerRef} className={className} />
}

const KatexRenderer: React.FC<KatexRendererProps> = ({ text, className = '' }) => {
  const normalizedText = text.normalize ? text.normalize('NFC') : text

  // Parse text into parts
  const parts = useMemo(() => parseTextWithMath(normalizedText), [normalizedText])

  if (!text) return null

  // No math found - render as plain text
  if (parts.length === 0 || (parts.length === 1 && parts[0].type === 'text')) {
    return (
      <span className={className}>
        {renderPlainText(normalizedText, 'plain')}
      </span>
    )
  }

  // Render mixed content
  return (
    <span className={className}>
      {parts.map((part) => {
        if (part.type === 'math') {
          return <KaTeXMath key={part.key} math={part.content} />
        }
        if (part.type === 'frac' && part.numerator !== undefined && part.denominator !== undefined) {
          return (
            <CssFraction
              key={part.key}
              numerator={part.numerator}
              denominator={part.denominator}
            />
          )
        }
        return (
          <span key={part.key}>
            {renderPlainText(part.content, part.key)}
          </span>
        )
      })}
    </span>
  )
}

export default KatexRenderer
